/**
 * وسيط عرض ذكيّ: يعرض <video> إن كان الرابط فيديو (mp4/webm/mov)، و<img> إن كان صورة.
 * يُستخدم في بلوكات الهوم كي يقبل كلّ بلوك صورة أو فيديو من نفس الحقل بلا تغيير في القاعدة.
 *
 * سياسة الحمل: لا تشغيل تلقائيّ أبداً. يظهر البوستر مع زرّ تشغيل،
 * وعند نقر المستخدم يبدأ الفيديو بالصوت (fallback صامت إن رفض المتصفّح).
 */
import * as React from "react";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { detectMediaKind } from "@/lib/media-kind";
import {
  VideoControlsBar,
  videoControlButtonClass,
  videoControlIconClass,
} from "@/components/media/VideoControlsBar";

import { useIsMobile } from "@/hooks/use-mobile";

type Props = {
  src: string;
  alt?: string;
  className?: string;
  /** بوستر للفيديو (اختياريّ) */
  poster?: string;
  style?: React.CSSProperties;
  /** اجعل الفيديو يشتغل تلقائيّاً على الموبايل أيضاً (الهيرو مثلاً) */
  autoplayOnMobile?: boolean;
  /** تشغيل يدويّ فقط — يظهر البوستر أوّلاً ولا يبدأ الفيديو إلّا عند الضغط */
  manualPlay?: boolean;
};

export function isVideoUrl(url: string | undefined | null) {
  return detectMediaKind(url) === "video";
}

export function AutoMedia({
  src,
  alt = "",
  className,
  poster,
  style,
  autoplayOnMobile = false,
  manualPlay = true,
}: Props) {
  const isMobile = useIsMobile();
  const [playing, setPlaying] = React.useState(false);
  const [paused, setPaused] = React.useState(false);
  // التشغيل التلقائيّ صامت (سياسة المتصفّحات)؛ التشغيل بنقرة المستخدم بصوت.
  const [soundOn, setSoundOn] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  React.useEffect(() => {
    if (!playing) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = !soundOn;
    if (soundOn) v.volume = 1;
    const p = v.play();
    if (p && typeof p.catch === "function")
      p.catch(() => {
        // رفض الصوت → إعادة المحاولة صامتاً
        setSoundOn(false);
        v.muted = true;
        void v.play().catch(() => {});
      });
  }, [playing, soundOn]);

  if (!src) return null;


  if (!isVideoUrl(src)) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading="lazy"
        decoding="async"
      />
    );
  }

  const gated = (isMobile && !autoplayOnMobile && !playing) || (manualPlay && !playing);

  if (gated) {
    return (
      <>
        {poster ? (
          <img
            src={poster}
            alt={alt}
            className={className}
            style={style}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            className={`${className ?? ""} bg-onyx`}
            style={style}
            role="img"
            aria-label={alt || undefined}
          />
        )}
        <VideoControlsBar size="sm">
          <button
            type="button"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSoundOn(true);
              setPlaying(true);
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSoundOn(true);
              setPlaying(true);
            }}
            aria-label="تشغيل الفيديو"
            className={videoControlButtonClass("sm")}
          >
            <Play className={`${videoControlIconClass("sm")} translate-x-[1px]`} fill="currentColor" />
          </button>
        </VideoControlsBar>

      </>
    );
  }

  return (
    <>
    <video
      key={src}
      ref={videoRef}
      src={src}
      className={className}
      style={style}
      poster={poster || undefined}
      autoPlay
      muted={!soundOn}
      loop
      playsInline
      preload="metadata"
      aria-label={alt || undefined}
    />
    {/* شريط تحكّم موحَّد: تشغيل ثمّ صوت */}
    <VideoControlsBar size="sm">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const v = videoRef.current;
          if (!v) return;
          if (v.paused) {
            setSoundOn(true);
            v.muted = false;
            v.volume = 1;
            void v.play().catch(() => {
              setSoundOn(false);
              v.muted = true;
              void v.play().catch(() => {});
            });
            setPaused(false);
          } else {
            v.pause();
            setPaused(true);
          }
        }}
        aria-label={paused ? "تشغيل الفيديو" : "إيقاف الفيديو"}
        className={videoControlButtonClass("sm")}
      >
        {paused ? (
          <Play className={`${videoControlIconClass("sm")} translate-x-[1px]`} fill="currentColor" />
        ) : (
          <Pause className={videoControlIconClass("sm")} fill="currentColor" />
        )}
      </button>
      {!paused ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const v = videoRef.current;
            const next = !soundOn;
            setSoundOn(next);
            if (v) {
              v.muted = !next;
              if (next) {
                v.volume = 1;
                void v.play().catch(() => {
                  setSoundOn(false);
                  v.muted = true;
                });
              }
            }
          }}
          aria-label={soundOn ? "كتم الصوت" : "تشغيل الصوت"}
          className={videoControlButtonClass("sm")}
        >
          {soundOn ? (
            <Volume2 className={videoControlIconClass("sm")} />
          ) : (
            <VolumeX className={videoControlIconClass("sm")} />
          )}
        </button>
      ) : null}
    </VideoControlsBar>

    </>
  );

}
